import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

declare global {
  interface Window {
    __analyticsLoaders?: {
      googleAnalytics: () => void;
      microsoftClarity: () => void;
      metricool: () => void;
      mailerlite: () => void;
    };
    gtag?: (...args: unknown[]) => void;
  }
}

// A third-party script failing to load should never break the site itself.
function safeLoad(loader?: () => void): void {
  try {
    loader?.();
  } catch {
    // Intentionally swallowed - see comment above.
  }
}

const PRIVACY_POLICY_LINK = '<a href="/privacy-policy">privacybeleid</a>';

export function initCookieConsent(): void {
  CookieConsent.run({
    guiOptions: {
      consentModal: {
        layout: 'box',
        position: 'bottom left',
        equalWeightButtons: true,
      },
      preferencesModal: {
        layout: 'box',
        equalWeightButtons: true,
      },
    },
    categories: {
      // Nothing on this site actually needs a "necessary" cookie today, but
      // the category is kept for structural clarity in the preferences
      // panel (readOnly: users can't disable something that isn't loading
      // anything in the first place).
      necessary: {
        readOnly: true,
        enabled: true,
      },
      // GA, Clarity, Metricool, and MailerLite are all treated the same way
      // here - none of them are necessary for the site to function, so none
      // of them load until the visitor explicitly consents. Modelled as
      // named services (rather than one opaque "analytics" toggle) so the
      // preferences panel names the actual third parties involved.
      analytics: {
        services: {
          google_analytics: {
            label: 'Google Analytics',
            onAccept: () => safeLoad(window.__analyticsLoaders?.googleAnalytics),
          },
          microsoft_clarity: {
            label: 'Microsoft Clarity',
            onAccept: () => safeLoad(window.__analyticsLoaders?.microsoftClarity),
          },
          metricool: {
            label: 'Metricool',
            onAccept: () => safeLoad(window.__analyticsLoaders?.metricool),
          },
          mailerlite: {
            label: 'MailerLite',
            onAccept: () => safeLoad(window.__analyticsLoaders?.mailerlite),
          },
        },
      },
    },
    language: {
      default: 'nl',
      translations: {
        nl: {
          consentModal: {
            title: 'Deze website gebruikt cookies',
            description:
              `We gebruiken noodzakelijke cookies om de website te laten werken. Met jouw toestemming gebruiken we ook analytische cookies (Google Analytics, Microsoft Clarity, Metricool) en cookies van onze nieuwsbriefwidget (MailerLite) om te begrijpen hoe de website wordt gebruikt. Lees ons ${PRIVACY_POLICY_LINK} voor meer informatie.`,
            acceptAllBtn: 'Alles accepteren',
            acceptNecessaryBtn: 'Alleen noodzakelijke',
            showPreferencesBtn: 'Voorkeuren beheren',
          },
          preferencesModal: {
            title: 'Cookievoorkeuren',
            acceptAllBtn: 'Alles accepteren',
            acceptNecessaryBtn: 'Alleen noodzakelijke',
            savePreferencesBtn: 'Voorkeuren opslaan',
            closeIconLabel: 'Sluiten',
            serviceCounterLabel: 'dienst(en)',
            sections: [
              {
                description:
                  'We gebruiken cookies om de basisfunctionaliteit van de website te garanderen en om, met jouw toestemming, het gebruik van de website te analyseren.',
              },
              {
                title: 'Noodzakelijke cookies',
                description:
                  'Essentieel voor het correct functioneren van de website. Kunnen niet worden uitgeschakeld.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Analytische cookies',
                description:
                  'Helpen ons te begrijpen hoe bezoekers de website gebruiken, en worden gebruikt door onze nieuwsbriefwidget. Alleen actief na jouw toestemming.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Meer informatie',
                description: `Bekijk ons ${PRIVACY_POLICY_LINK} voor meer details.`,
              },
            ],
          },
        },
      },
    },
  });
}

export function showCookiePreferences(): void {
  CookieConsent.showPreferences();
}
